import { Badge, Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { Link } from '@reach/router';
import moment from 'moment';
import { AccessPoint } from './query';

interface AccessPointCardProps extends AccessPoint {}

export const statusColorScheme = {
  CONFIRMED: 'green',
  ACTIVE: 'green',
  INACTIVE: 'yellow',
  DRAFT: 'gray',
  PENDING: 'yellow',
  WAITLIST: 'blue',
  REJECTED: 'red',
  RETRY: 'red',
};

export const resolveAccessPointDisplayFields = ({
  dropPoint,
  submitTimestamp,
  identificationSuccess,
  identificationTries,
}: AccessPoint) => {
  const stationSubmitTimestamp = dropPoint.submitTimestamp;
  const stationIdentificationSuccess = dropPoint.identificationSuccess;
  const stationIdentificationTries = dropPoint.identificationTries;

  return {
    submitTimestamp: stationSubmitTimestamp ?? submitTimestamp,
    identificationSuccess:
      typeof stationIdentificationSuccess === 'boolean'
        ? stationIdentificationSuccess
        : typeof identificationSuccess === 'boolean'
          ? identificationSuccess
          : null,
    identificationTries:
      typeof stationIdentificationTries === 'number'
        ? stationIdentificationTries
        : typeof identificationTries === 'number'
          ? identificationTries
          : null,
  };
};

export const resolveAmazonLifecycleBadge = (hubCounter?: AccessPoint['dropPoint']['hubCounter']) => {
  if (!hubCounter?.status) {
    return { label: 'Amazon: Nicht angelegt', colorScheme: 'gray' };
  }

  return {
    label: `Amazon: ${hubCounter.status}`,
    colorScheme: statusColorScheme[hubCounter.status] ?? 'gray',
  };
};

export const buildAccessPointStatusBadges = ({ status, isActive, accessPointID, dropPoint }: AccessPoint) => {
  const badges = [
    { label: `UPS: ${status}`, colorScheme: statusColorScheme[status] ?? 'gray' },
    resolveAmazonLifecycleBadge(dropPoint?.hubCounter),
    { label: dropPoint.isActive ? 'DropPoint aktiv' : 'DropPoint inaktiv', colorScheme: dropPoint.isActive ? 'green' : 'red' },
  ];

  if (accessPointID) {
    badges.push({ label: isActive ? 'UPS aktiv' : 'UPS inaktiv', colorScheme: isActive ? 'green' : 'red' });
  }

  return badges;
};

export const AccessPointCard = (props: AccessPointCardProps) => {
  const { dropPoint } = props;
  const { colorMode } = useColorMode();
  const { submitTimestamp, identificationSuccess, identificationTries } = resolveAccessPointDisplayFields(props);
  const statusBadges = buildAccessPointStatusBadges(props);

  return (
    <Box
      as={Link}
      to={`/users/${dropPoint.user.id}`}
      width="100%"
      rounded="md"
      borderWidth="1px"
      py="4"
      px="6"
      _hover={{ boxShadow: 'md', backgroundColor: colorMode === 'light' ? 'white' : 'gray.700' }}
    >
      <Flex flexWrap="wrap" mb="2" gap="2">
        {statusBadges.map((badge) => (
          <Badge key={badge.label} colorScheme={badge.colorScheme}>
            {badge.label}
          </Badge>
        ))}
      </Flex>
      <Text mb="1" fontFamily="heading" fontSize="lg" fontWeight="bold">
        {dropPoint.locationName || `${dropPoint.user.firstName} ${dropPoint.user.lastName}`}
      </Text>
      {dropPoint.address && (
        <Text mb="2">
          {dropPoint.address.street} {dropPoint.address.houseNumber}
          <br />
          {dropPoint.address.postalCode} {dropPoint.address.city}
        </Text>
      )}
      <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
        Registrierungsdatum: {moment(dropPoint.createdAt).format('DD.MM.YYYY HH:mm')}
      </Text>
      {submitTimestamp && (
        <>
          <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
            Eingereicht am: {moment(submitTimestamp).format('DD.MM.YYYY HH:mm')}
          </Text>
          <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
            Ident Prüfungs-Status: {identificationSuccess ? 'Erfolgreich' : identificationTries > 1 ? 'Fehlgeschlagen' : 'Ausstehend'}
          </Text>
        </>
      )}
    </Box>
  );
};
